import { useEffect, useState } from 'react'
import * as React from 'react'
import { Grid } from '@mui/material'
import { useRouter } from 'next/router'

import Accounts, { Account } from '@ecm/data/Account'
import { getSha256Hash } from '@ecm/data/Hash'
import { formatErrors, HubSpotResponse, isOnlyInvalidEmailError } from '@ecm/data/HubSpot'
import { Lead } from '@ecm/data/Lead'
import { LeadAddressInfo } from '@ecm/data/LeadAddressInfo'
import { runClientEff } from '@ecm/effect'
import { Button } from '@ecm/ui/element/Button'
import { validateEmail } from '@ecm/ui/element/Form/Validation'
import { BaseInput } from '@ecm/ui/element/input/Input'
import useOrderInfo from '@ecm/ui/hook/UseOrderInfo'

type EmailOnlyLeadFormProps = {
  onSuccess: () => void
  onFailure?: () => void
  className?: string
  source: string
  isMobile?: boolean
  leadAddressInfo?: LeadAddressInfo
}

export const EmailOnlyLeadForm = ({
  className,
  onSuccess,
  onFailure,
  source,
  isMobile = false,
  leadAddressInfo,
}: EmailOnlyLeadFormProps) => {
  const router = useRouter()

  const { account } = useOrderInfo(router, false, false, false, false)

  const [emailInputValue, setEmailInputValue] = useState<string>('')
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [emailValidationMessage, setEmailValidationMessage] = useState<string>('')

  const handleInputChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    setEmailInputValue(e.currentTarget.value)
  }

  useEffect(() => {
    if (account?.email) {
      setEmailInputValue(account.email)
    }
  }, [account])

  const handleBlur = () => {
    const validationMessage = validateEmail(emailInputValue)
    setEmailValidationMessage(validationMessage)
  }

  const handleSubmit = () => {
    const validationMessage = validateEmail(emailInputValue)
    setEmailValidationMessage(validationMessage)

    if (!validationMessage) {
      setSubmitting(true)
      runClientEff(async eff => {
        try {
          const lead: Lead = {
            addressInfo: leadAddressInfo,
            email: emailInputValue,
            lifeCycleStage: 'Lead',
            productFamily: 'ECM',
          }
          const formId = eff.ask().hubSpot.formIds.emailOnlyForm
          const response: HubSpotResponse = await eff.submitGenericLead(lead, formId, source)
          if (response.errors && !isOnlyInvalidEmailError(response.errors)) {
            if (onFailure) {
              onFailure()
            }
            console.error(formatErrors(response.errors))
            setSubmitting(false)
          } else {
            eff.setStorageItem<Account>(
              'account',
              Accounts.account({
                email: emailInputValue,
                firstName: '',
                lastName: '',
              })
            )
            eff.trackGa({
              cp_customer_email: getSha256Hash(emailInputValue),
              event: 'addressLeadFormSuccess',
            })
            onSuccess()
          }
        } catch (error) {
          if (onFailure) {
            onFailure()
          }
          console.error(error)
          setSubmitting(false)
        }
      })
    }
  }

  return (
    <Grid container alignItems="stretch" justifyContent="space-between">
      <BaseInput
        className={className}
        /*
            Build id based on address street info and view type to avoid warnings with duplicated ids
            The issues appear for desktop version because we render also mobile version as hidden elements
        */
        id={`email_${leadAddressInfo?.street.replace(/ /g, '_').toLocaleLowerCase()}_${
          isMobile ? 'm' : 'd'
        }`}
        label="Email"
        name="email"
        type="email"
        width="55%"
        onChange={handleInputChange}
        initialValue={account?.email}
        onEnter={handleSubmit}
        error={emailValidationMessage}
        onBlur={handleBlur}
      />
      <Button
        submitting={submitting}
        onClick={handleSubmit}
        primary
        width="40%"
        margin="0 !important"
        narrow
      >
        Use address
      </Button>
    </Grid>
  )
}
export default EmailOnlyLeadForm
